/**
 * 轨迹回放
 */
import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Form, Button, Slider, Row, Col, Input, message, Checkbox, Switch, Space, Drawer, Select, Card } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { groupBy } from 'lodash';
import dayjs from 'dayjs';
import gcoord from 'gcoord';
import {
  currentLocationRequest,
  dayGpsRequest,
  dayAlarmRequest,
  DayInfoRequestParams,
  gpsListExportPath,
  historyDayGpsRequest
} from '@/service/trackPlayback';
import { adasCurrentLocationRequest } from '@/service/adasTrackPlayback';
import { carLastLocationRequest, findOneCarRequest } from '@/service/carManage';
import { importMap } from '@/utils/map';
import { ownRound } from '@/utils/math';
import { formatTime } from '@/utils/formatTime';
import CarTree from '@/components/carTree';
import DatePicker from '@/components/datePicker';
import ExportButton from '@/components/exportButton';
import CommonTable from '@/components/commonTable';
import LoadingWrapper from '@/components/loadingWrapper';
import { filterNotInChina, filterAdjoinPoint } from '@/utils/gps';
import { getAlarmTypeDisplay } from '@/utils/alarm';
import { getAdasAlarmType } from '@/utils/adasAlarm';
import carPointImg from '@/assets/image/forward.png';
import styles from './index.module.scss';
import {
  FastForwardFilled,
  PauseCircleFilled,
  PlayCircleFilled,
  CloseCircleFilled,
  FullscreenOutlined
} from '@ant-design/icons';

const TrackPlayback = () => {
  const tabPagesStore = useSelector((state: any) => state.tabPagesStore);
  const { activeKey, tabParams } = tabPagesStore;
  const mapRef: any = useRef(null); // 存储地图实例
  const mapReadyRef: any = useRef(null); // 地图瓦片加载完成的Promise
  const ruleRef: any = useRef([]); // 存储尺子实例(可能会有多个)
  const animationMarkerRef: any = useRef(null); // 存储轨迹回放的marker
  const trackAniRef: any = useRef({}); // 存储轨迹回放动画数据
  const alarmLabelsRef: any = useRef({ 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 8: [], 9: [] }); // 存储报警的label信息
  const [form] = Form.useForm();
  const [alarmLabelsChecked, setAlarmLabelsChecked] = useState<any>({
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
    6: false,
    8: false,
    9: false
  }); // 是否勾选报警的label
  const [itineraryList, setItineraryList] = useState<any[]>([]); // 行程数据
  const [loading, setLoading] = useState(false); // 数据是否在加载中
  const [hasSearch, setHasSearch] = useState(false); // 是否搜索过
  const [adasSwitch, setAdasSwitch] = useState(Boolean)
  const [operationStartTime, SetOperationStartTime] = useState(''); // 运营开始时间
  const [operationStoptTime, SetOperationStoptTime] = useState(''); // 运营停驶时间
  const [runningTime, setRunningTime] = useState(''); // 运行时长
  const [downTime, setDownTime] = useState(''); // 停驶时长
  const [stopTimes, setStopTimes] = useState(''); // 停驶次数
  const [MaxtopTimes, setMaxtopTimes] = useState('');  //最长停驶时长
  const [MinStopTimes, setMinStopTimes] = useState('');  //最短停驶时长
  const [progressValue, setProgressValue] = useState(Number);
  const [progressMaxValue, setProgressMaxValue] = useState(Number);

  const [currentItiner, setCurrentItiner] = useState<any>(); // 当前选中的行程数据
  const [currentItinerId, setCurrentItinerId] = useState(''); // 当前选中的行程数据d
  const [open, setOpen] = useState(false); //抽屉状态 
  const [travelId, setTravelId] = useState<any>([]); // 行驶Id的显示 
  const [currentId, setcurrentId] = useState(''); // 当前行程段id
  const [currentData, setcurrentData] = useState<any>([]) //当前行程段gpsData
  const [currentSpeed, setCurrentSpeed] = useState('');// 速度
  const [isPlaying, setIsPlaying] = useState(false); // 是否播放中
  const [val, setVal] = useState(''); // 存储value
  const [clicking, setClicking] = useState(false); //设置点击之后样式
  const [groupGpsData, setGroupGpsData] = useState<any>([]); //存储行程数据
  const [showSwitch, setShowSwitch] = useState<Boolean>(true)
  const [selectCarInfo, setSelectCarInfo] = useState<{ [key: string]: any }>({})

  useEffect(() => {
    importMap(initMap);
  }, [])

  // 监听跳转时携带参数的形式
  useEffect(() => {
    tabParamsSearch()
  }, [activeKey, tabParams]);

  // 跳转时携带参数时搜索
  const tabParamsSearch = async () => {
    let mapSuccess: any = false;
    if (window.BMap && mapRef.current) {
      mapSuccess = true;
    } else {
      mapSuccess = await importMap(initMap);
    }
    if (mapSuccess && activeKey === '/car-admin/track-playback' && tabParams) {
      const newTabParams = {
        plateNumber: tabParams.plateNumber,
        trickDate: dayjs(tabParams.day),
      };
      form.setFieldsValue(newTabParams);
      form.submit();
    }
  }

  // 地图初始化（百度地图）
  const initMap = async () => {
    try {
      mapRef.current = new window.BMap.Map('trackMap');
      const center = new window.BMap.Point(116.368904, 39.913423);
      mapRef.current.centerAndZoom(center, 15);
      mapRef.current.enableScrollWheelZoom();
      // 缩放控件（对应 AMap.ToolBar）
      mapRef.current.addControl(new window.BMap.NavigationControl({
        anchor: window.BMAP_ANCHOR_BOTTOM_LEFT,
      }));
      // 比例尺控件（对应 AMap.Scale）
      mapRef.current.addControl(new window.BMap.ScaleControl());
      // 地图类型切换控件（对应 AMap.MapType）
      mapRef.current.addControl(new window.BMap.MapTypeControl({
        mapTypes: [window.BMAP_NORMAL_MAP, window.BMAP_HYBRID_MAP],
      }));
      // 等待瓦片加载完成，确保后续 setCenter/centerAndZoom 能正常生效
      mapReadyRef.current = new Promise<void>((resolve) => {
        mapRef.current.addEventListener('tilesloaded', function onTilesLoaded() {
          mapRef.current.removeEventListener('tilesloaded', onTilesLoaded);
          resolve();
        });
      });
    } catch (error) {
      message.error('地图初始化失败');
    }
  }


  // ADAS开关
  const adasSwitchover = (checked: boolean) => {
    setAdasSwitch(checked)
  }

  useEffect(() => {
    if (selectCarInfo.metaId) {
      getSelectCarLocation(selectCarInfo)
    }
  }, [adasSwitch])

  // 获取选择车辆的位置(在地图中心显示)
  const getSelectCarLocation = async (car: any, type?: Boolean) => {
    const adasSwitchType = type === undefined ? adasSwitch : type
    const res = await carLastLocationRequest({ carId: car.metaId, plateNumber: car.title_base, isKls: adasSwitchType ? false : true });

    const locationData = res?.data?.data || {};
    if (locationData.lon && locationData.lat) {
      const transformGcoord = gcoord.transform([locationData.lat, locationData.lon], gcoord.WGS84, gcoord.BD09);
      const point = new window.BMap.Point(transformGcoord[0], transformGcoord[1]);

      const icon = new window.BMap.Icon(carPointImg, new window.BMap.Size(36, 36), {
        imageSize: new window.BMap.Size(36, 36),
        anchor: new window.BMap.Size(18, 18),
      });
      const marker = new window.BMap.Marker(point, {
        icon,
      });
      // 等待地图瓦片加载完成后再设置中心，避免首次加载时 centerAndZoom 被忽略
      if (mapReadyRef.current) {
        await mapReadyRef.current;
      }
      mapRef.current.clearOverlays();
      mapRef.current.addOverlay(marker);
      mapRef.current.centerAndZoom(point, mapRef.current.getZoom());
    }
  }

  // 选择车辆
  const treeSelect = (selectCarInfo: any) => {
    const { selected, metaId, title, sn, jt808DeviceId } = selectCarInfo;
    console.log(selectCarInfo, '12312312312323123')
    cancelAllAlarmChecked();
    setSelectCarInfo(selectCarInfo)
    //此处selected为false视为选择车辆
    if (!selected) {

      let type = false;
      form.setFieldsValue({ plateNumber: title });
      setShowSwitch(!!(sn && jt808DeviceId));

      if (jt808DeviceId && sn) {
        getSelectCarLocation(selectCarInfo);
      } else if (sn || jt808DeviceId) {
        type = !!jt808DeviceId;
        adasSwitchover(type);
        getSelectCarLocation(selectCarInfo, type);
      }



    } else {
      form.setFieldsValue({ plateNumber: '' })
    }
  }

  // 信息弹窗展示（百度地图版）
  const showInfoWindow = (e: any) => {
    const target = e.target || e.currentTarget;
    const originData = target?.originData || {};
    const { speed, vehicleSpeed, reportedTime } = originData;
    const clickPoint = e.point || target.getPosition();

    const geocoder = new window.BMap.Geocoder();
    geocoder.getLocation(clickPoint, (result: any) => {
      const address = result ? result.address : '';
      const content = `GPS速度: ${speed}<br>车身速度: ${vehicleSpeed}<br>时间: ${formatTime(reportedTime, 'YYYY-MM-DD HH:mm:ss')}<br>地址: ${address}`;
      const infoWindow = new window.BMap.InfoWindow(content);
      mapRef.current.openInfoWindow(infoWindow, clickPoint);
    });
  }

  const getDayAdasGps = async (params: DayInfoRequestParams) => {
    const res = await dayGpsRequest({ ...params, isKls: false });
    const gpsData = res.data?.data.gps || [];
    const beginTime = formatTime(res.data?.data.beginTime);
    const endTime = formatTime(res.data?.data.endTime);
    SetOperationStartTime(beginTime)
    SetOperationStoptTime(endTime)
    setRunningTime(res.data?.data.operateTime)
    setDownTime(res.data?.data.stoppingTime)
    setStopTimes(res.data?.data.stopCount)
    setMaxtopTimes(res.data?.data.longStoppingTime)
    setMinStopTimes(res.data?.data.shortStoppingTime)
    const filterGpsData = gpsData.filter(filterNotInChina).filter(filterAdjoinPoint);
    if (filterGpsData.length) {
      // 根据GPS数据生成行程列表数据
      const groupGpsData = groupBy(filterGpsData, 'itineraryId');
      const newItineraryList: any = [];
      Object.keys(groupGpsData).reverse().forEach((item: any) => {
        newItineraryList.push({
          id: item,
          gpsData: groupGpsData[item] || [],
        });
      })
      // 逆地理编码获取停驶位置
      newItineraryList.map((item: any, index: any) => {
        const lastGps = item.gpsData[item.gpsData.length - 1];
        const transformGcoord = gcoord.transform([lastGps.lat, lastGps.lon], gcoord.WGS84, gcoord.BD09);
        const pt = new window.BMap.Point(transformGcoord[0], transformGcoord[1]);
        const geocoder = new window.BMap.Geocoder();
        geocoder.getLocation(pt, (result: any) => {
          if (result) {
            newItineraryList[index].describeLocation = result.address;
          }
          if (index === newItineraryList.length - 1) {
            setItineraryList([...newItineraryList]);
          }
        });
      })
      setGroupGpsData(newItineraryList)
      drawLinesOnMap(filterGpsData)
    } else {
      setItineraryList([]);
    }

  }
  const getDayAebGps = async (params: DayInfoRequestParams) => {

    //非adas GPS点位信息
    //根据搜索日期调用不同接口
    let dateValue = Number(params.trickDate.split('-').map(item => item.padStart(2, '0')).join(''))
    if (dateValue <= 20231001) {
      var res = await historyDayGpsRequest(params);
    } else {
      var res = await dayGpsRequest({ ...params, isKls: true });
    }
    const gpsData = res.data?.data.gps || [];
    const beginTime = formatTime(res.data?.data.beginTime);
    const endTime = formatTime(res.data?.data.endTime);
    SetOperationStartTime(beginTime)
    SetOperationStoptTime(endTime)
    setRunningTime(res.data?.data.operateTime)
    setDownTime(res.data?.data.stoppingTime)
    setStopTimes(res.data?.data.stopCount)
    setMaxtopTimes(res.data?.data.longStoppingTime)
    setMinStopTimes(res.data?.data.shortStoppingTime)
    const filterGpsData = gpsData.filter(filterNotInChina).filter(filterAdjoinPoint);
    if (filterGpsData.length) {
      // 根据GPS数据生成行程列表数据
      const groupGpsData = groupBy(filterGpsData, 'itineraryId');
      const newItineraryList: any = [];
      const ll: any = [];
      Object.keys(groupGpsData).reverse().forEach((item: any) => {
        newItineraryList.push({
          id: item,
          gpsData: groupGpsData[item] || [],
          describeLocation: ""
        });
      })
      // 逆地理编码获取停驶位置
      newItineraryList.map((item: any, index: any) => {
        const lastGps = item.gpsData[item.gpsData.length - 1];
        const transformGcoord = gcoord.transform([lastGps.lat, lastGps.lon], gcoord.WGS84, gcoord.BD09);
        const pt = new window.BMap.Point(transformGcoord[0], transformGcoord[1]);
        const geocoder = new window.BMap.Geocoder();
        geocoder.getLocation(pt, (result: any) => {
          if (result) {
            newItineraryList[index].describeLocation = result.address;
          }
          if (index === newItineraryList.length - 1) {
            setItineraryList([...newItineraryList]);
          }
        });
      })
      setGroupGpsData(newItineraryList)
      drawLinesOnMap(filterGpsData);
    } else {
      setItineraryList([]);
    }
  }

  const drawLinesOnMap = (filterGpsData: any) => {
    mapRef.current.clearOverlays()
    const paths: any = [];
    filterGpsData.forEach((item: any) => {
      const transformGcoord = gcoord.transform([item.lat, item.lon], gcoord.WGS84, gcoord.BD09);
      paths.push(new window.BMap.Point(transformGcoord[0], transformGcoord[1]));
    })
    // 根据gps点位画线
    const polyline = new window.BMap.Polyline(paths, {
      strokeWeight: 5,
      strokeColor: '#BB2299',
    });
    mapRef.current.addOverlay(polyline);

    // 绘制GPS点位
    paths.forEach((item: any, index: number) => {
      let content = `<div class=${styles.gpsPoint} />`;
      let offsetX = -6;
      let offsetY = -6;
      let zIndex = 120;
      if (index === 0) {
        content = `<div class=${styles.textIcon}>起</div>`;
        offsetX = -10;
        offsetY = -10;
        zIndex = 122;
      }
      if (index === paths.length - 1) {
        content = `<div class=${styles.textIcon}>终</div>`;
        offsetX = -10;
        offsetY = -10;
        zIndex = 122;
      }
      const label = new window.BMap.Label(content, {
        position: item,
        offset: new window.BMap.Size(offsetX, offsetY),
      });
      label.setStyle({
        border: 'none',
        backgroundColor: 'transparent',
        zIndex,
      });
      label.setTitle(`GPS速度: ${ownRound(filterGpsData[index].speed, 1)}; 时间: ${formatTime(filterGpsData[index].reportedTime, 'YYYY-MM-DD HH:mm:ss')}`);
      label.originData = filterGpsData[index];
      label.addEventListener('click', (e: any) => {
        // 构造与 showInfoWindow 兼容的事件对象
        const evtObj = { target: label, point: item };
        showInfoWindow(evtObj);
      });
      mapRef.current.addOverlay(label);
    })
    mapRef.current.setCenter(paths[0]);

  }

  // 获取adas GPS点位信息(连线显示)
  const getDayGps = async (params: DayInfoRequestParams) => {
    if (adasSwitch) {
      getDayAdasGps(params)
    } else {
      getDayAebGps(params)
    }
  }

  const getDayAdasAlarm = async (params: DayInfoRequestParams) => {
    try {
      setLoading(true);
      const res = await dayAlarmRequest({ ...params, isKls: false });
      const alarmData = res.data?.data || [];
      if (alarmData.length) {
        const data = alarmData.filter(filterNotInChina).filter(filterAdjoinPoint);
        data.forEach((item: any) => {
          const transformGcoord = gcoord.transform([item.lat, item.lon], gcoord.WGS84, gcoord.BD09);
          const point = new window.BMap.Point(transformGcoord[0], transformGcoord[1]);
          // 使用本地图标替代高德静态资源
          const alarmIcon = new window.BMap.Icon('/images/map/pin-sprite.png', new window.BMap.Size(18, 23), {
            imageSize: new window.BMap.Size(360, 23),
          });
          alarmIcon.setImageOffset(new window.BMap.Size(0, 0));
          const alarmMarker = new window.BMap.Marker(point, {
            icon: alarmIcon,
          });
          alarmMarker.originData = item;
          alarmMarker.addEventListener('click', (e: any) => {
            showInfoWindow({ target: alarmMarker, point });
          });
          mapRef.current.addOverlay(alarmMarker);
          // 存储报警信息文本标注，勾选信息时显示
          const labelString = `<div class=${styles.alarmLable}>报警类型: ${getAdasAlarmType(item.alarmType)}
            <br/>速度: ${item.speed}
            <br/>时间: ${formatTime(item.reportedTime, 'YYYY-MM-DD HH:mm:ss')}</div>`;
          // 创建文本标注（百度 Label）
          const label = new window.BMap.Label(labelString, {
            position: point,
            offset: new window.BMap.Size(-10, -10),
          });
          label.setStyle({
            border: 'none',
            backgroundColor: 'transparent',
            zIndex: 121,
          });
          switch (item.alarmType) {
            case 9:
              alarmLabelsRef.current[1].push(label);
              break;
            case 10:
              alarmLabelsRef.current[2].push(label);
              break;
            case 11:
              alarmLabelsRef.current[3].push(label);
              break;
            case 12:
              alarmLabelsRef.current[4].push(label);
              break;
          }
        });
        setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  }
  const getDayAebAlarm = async (params: DayInfoRequestParams) => {
    try {
      setLoading(true);
      const res = await dayAlarmRequest({ ...params, isKls: true });
      const alarmData = res.data?.data || [];
      if (alarmData.length) {
        const data = alarmData.filter(filterNotInChina).filter(filterAdjoinPoint);
        data.forEach((item: any) => {
          const transformGcoord = gcoord.transform([item.lat, item.lon], gcoord.WGS84, gcoord.BD09);
          const point = new window.BMap.Point(transformGcoord[0], transformGcoord[1]);

          // 逆地理编码获取地址
          const geocoder = new window.BMap.Geocoder();
          geocoder.getLocation(point, (result: any) => {
            const address = result ? result.address : '';

            // 使用本地图标替代高德静态资源
            const alarmIcon = new window.BMap.Icon('/images/map/pin-sprite.png', new window.BMap.Size(18, 23), {
              imageSize: new window.BMap.Size(360, 23),
            });
            alarmIcon.setImageOffset(new window.BMap.Size(0, 0));
            const alarmMarker = new window.BMap.Marker(point, {
              icon: alarmIcon,
            });
            alarmMarker.originData = item;
            alarmMarker.addEventListener('click', (e: any) => {
              showInfoWindow({ target: alarmMarker, point });
            });
            mapRef.current.addOverlay(alarmMarker);
            // 存储报警信息文本标注，勾选信息时显示
            const labelString = `<div class=${styles.alarmLable}>报警类型: ${item.alarmTypeStr}
                  ${item.vehicleSpeed === null ? '' : `<br>车身速度: ${ownRound(item.vehicleSpeed, 1)}`}
                  <br/>时间: ${formatTime(item.reportedTime, 'YYYY-MM-DD HH:mm:ss')}
                  ${item.gpsSpeed === null ? '' : `<br>gps速度: ${ownRound(item.gpsSpeed, 1)}`}
                  ${item.riskLevel === null || item.riskLevel !== 1 && item.riskLevel !== 2 && item.riskLevel !== 3 ? '' : `<br>风险等级: ${getRiskLevel(item.riskLevel)}`}
                  ${item.sateNum === null ? '' : `<br>4G信号强度: ${getSateNum(item.sateNum)}`}
                  ${item.lat === null ? '' : `<br>地址: ${address}`}
                  </div>`;
            // 创建文本标注（百度 Label）
            const label = new window.BMap.Label(labelString, {
              position: point,
              offset: new window.BMap.Size(-10, -10),
            });
            label.setStyle({
              border: 'none',
              backgroundColor: 'transparent',
              zIndex: 121,
            });
            switch (item.alarmType) {
              case 1:
                alarmLabelsRef.current[1].push(label);
                break;
              case 2:
                alarmLabelsRef.current[2].push(label);
                break;
              case 3:
                alarmLabelsRef.current[3].push(label);
                break;
              case 4:
                alarmLabelsRef.current[4].push(label);
                break;
              case 5:
                alarmLabelsRef.current[5].push(label);
                break;
              case 7:
                // BSD预警
                alarmLabelsRef.current[8].push(label);
                break;
              case 10:
                // BSD制动
                alarmLabelsRef.current[9].push(label);
                break;
              default:
                alarmLabelsRef.current[6].push(label);
            }
          });
        });
        setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  }


  // 获取报警点位信息(存储报警的label信息)
  const getDayAlarm = async (params: DayInfoRequestParams) => {
    if (adasSwitch) {
      getDayAdasAlarm(params)
    } else {
      getDayAebAlarm(params)
    }
  }

  // 处理搜索参数
  const operateSearchParams = async (params: any, isExport?: boolean) => {
    const newParams = { ...params };
    newParams.plateNumber = (params.plateNumber || '').trim();
    let carId = '';
    if (newParams.plateNumber) {
      const carRes = await findOneCarRequest({
        plateNumber: newParams.plateNumber
      });
      carId = carRes.data?.data?.id;
    }
    newParams.trickDate = dayjs(params.trickDate).format('YYYY-MM-DD');
    if (isExport) {
      newParams.export = true;
      newParams.carId = carId;
    }
    return newParams;
  }
  //风险数据
  const getRiskLevel = (value: any) => {
    if (value == 1) {
      return '低风险'
    } else if (value == 2) {
      return '中风险'
    } else if (value == 3) {
      return '高风险'
    }
  }

  // 4G信号强度
  const getSateNum = (value: any) => {
    if (value == 0) {
      return '初始化'
    } else if (0 < value && value <= 4) {
      return '低'
    } else if (5 <= value && value <= 14) {
      return '中下等'
    } else if (15 <= value && value <= 25) {
      return '中等'
    } else if (value >= 26) {
      return '高'
    }
  }

  // 获取轨迹数据1
  const onSearch = async (values: any) => {
    if (!values.plateNumber) {
      message.info('请选择车辆');
    } else {
      cancelAllAlarmChecked();
      const params: any = await operateSearchParams(values);
      getDayGps(params);
      getDayAlarm(params);
      setHasSearch(true);
      setcurrentData([])  // 清空当前行驶段数据
      travelItineraryId('')  // 重新给行驶Id赋值 
    }
  }


  // 获取导出的参数
  const getExportParams = async () => {
    const params = form.getFieldsValue();
    if (!params.plateNumber) {
      message.info('请选择车辆');
      return;
    }
    return await operateSearchParams(params, true)
  }

  // 尺子工具（百度地图暂不支持）
  const openRule = () => {
    message.info('百度地图暂不支持尺子工具');
  }

  // ========== 轨迹回放动画相关（百度地图版，使用 requestAnimationFrame）==========

  /**
   * 启动沿路径移动动画
   * @param paths BMap.Point 数组
   * @param itineraryData 原始GPS数据（与paths一一对应）
   * @param id 行程 ID
   * @param startIndex 起始索引（拖动进度条时使用）
   */
  const startMoveAnimation = (paths: any[], itineraryData: any[], id: string, startIndex: number = 0) => {
    // 清理之前的动画 marker
    if (animationMarkerRef.current) {
      if (animationMarkerRef.current._animId) {
        cancelAnimationFrame(animationMarkerRef.current._animId);
      }
      mapRef.current.removeOverlay(animationMarkerRef.current);
    }

    // 创建动画 Label 作为移动标记（模拟原 AMap.Text）
    const animLabel = new window.BMap.Label(`<div class=${styles.gpsPointLarge} />`, {
      position: paths[startIndex],
      offset: new window.BMap.Size(-12, -12),
    });
    animLabel.setStyle({
      border: 'none',
      backgroundColor: 'transparent',
      zIndex: 122,
    });
    mapRef.current.addOverlay(animLabel);

    // 路过的轨迹标识为黑色
    const passedPolyline = new window.BMap.Polyline([], {
      strokeColor: "#000",
      strokeWeight: 5,
    });
    mapRef.current.addOverlay(passedPolyline);

    // 存储变为黑色的轨迹(用于后续删除)
    const newTrackAni = { ...trackAniRef.current };
    if (newTrackAni[id]) {
      newTrackAni[id].push(passedPolyline);
    } else {
      newTrackAni[id] = [passedPolyline];
    }
    trackAniRef.current = newTrackAni;

    // 动画状态
    let currentIndex = startIndex;
    const passedPath: any[] = [paths[startIndex]];
    let lastTimestamp = 0;
    const STEP_DURATION = 500; // 每段移动耗时 500ms，与原版一致
    let isPaused = false;

    const animate = (timestamp: number) => {
      if (isPaused) {
        animLabel._animId = requestAnimationFrame(animate);
        return;
      }
      if (!lastTimestamp) lastTimestamp = timestamp;
      const elapsed = timestamp - lastTimestamp;

      if (elapsed >= STEP_DURATION) {
        lastTimestamp = timestamp;
        currentIndex++;

        if (currentIndex >= paths.length) {
          // 动画结束
          setIsPlaying(false);
          return;
        }

        // 更新位置
        animLabel.setPosition(paths[currentIndex]);
        passedPath.push(paths[currentIndex]);
        passedPolyline.setPath(passedPath);
        mapRef.current.setCenter(paths[currentIndex]);
        setProgressValue(currentIndex);

        // 显示点位信息
        const matchData = itineraryData[currentIndex] || {};
        setCurrentSpeed(ownRound(matchData.speed, 1));
        const content = `GPS速度: ${ownRound(matchData.speed, 1)}<br>车身速度: ${ownRound(matchData.vehicleSpeed, 1)}<br>时间: ${formatTime(matchData.reportedTime, 'YYYY-MM-DD HH:mm:ss')}`;
        const infoWindow = new window.BMap.InfoWindow(content);
        mapRef.current.openInfoWindow(infoWindow, paths[currentIndex]);
      }

      animLabel._animId = requestAnimationFrame(animate);
    };

    // 显示起始点信息窗口
    const startData = itineraryData[startIndex] || {};
    const startContent = `GPS速度: ${ownRound(startData.speed, 1)}<br>车身速度: ${ownRound(startData.vehicleSpeed, 1)}<br>时间: ${formatTime(startData.reportedTime, 'YYYY-MM-DD HH:mm:ss')}`;
    const startInfoWindow = new window.BMap.InfoWindow(startContent);
    mapRef.current.openInfoWindow(startInfoWindow, paths[startIndex]);

    // 启动动画
    animLabel._animId = requestAnimationFrame(animate);

    // 扩展控制方法到 Label 上
    animLabel.pauseMove = () => { isPaused = true; };
    animLabel.resumeMove = () => { isPaused = false; };
    animLabel.stopMove = () => {
      if (animLabel._animId) {
        cancelAnimationFrame(animLabel._animId);
        animLabel._animId = null;
      }
    };
    animLabel.hide = () => {
      mapRef.current.removeOverlay(animLabel);
    };

    animationMarkerRef.current = animLabel;
  };

  // 拖动进度条
  const dragProgress = (value: number) => {
    // 停止当前动画
    if (animationMarkerRef.current) {
      animationMarkerRef.current.stopMove();
      animationMarkerRef.current.hide();
    }

    const paths: any = [];
    const newItineraryList = currentItiner.slice(value);
    currentItiner.forEach((item: any) => {
      const transformGcoord = gcoord.transform([item.lat, item.lon], gcoord.WGS84, gcoord.BD09);
      paths.push(new window.BMap.Point(transformGcoord[0], transformGcoord[1]));
    });

    // 使用 value 作为起始索引
    startMoveAnimation(paths, currentItiner, currentItinerId, value);
  }

  // 播放行程
  const onPlay = (id: string, itineraryData: any) => {
    if (isPlaying) {
      return
    }
    setIsPlaying(true); // 设置播放状态为true
    setProgressValue(0) //点击播放先清空
    console.log(itineraryData?.length, 'itineraryData?.length');
    if (!itineraryData?.length) {
      message.info('此行程没有数据');
      return;
    }

    console.log("该段轨迹数量：", itineraryData.length)
    setProgressMaxValue(itineraryData.length)
    setCurrentItiner(itineraryData)
    setCurrentItinerId(id)

    // 创建播放路线对象
    const paths: any = [];
    itineraryData.forEach((item: any) => {
      const transformGcoord = gcoord.transform([item.lat, item.lon], gcoord.WGS84, gcoord.BD09);
      paths.push(new window.BMap.Point(transformGcoord[0], transformGcoord[1]));
    })

    startMoveAnimation(paths, itineraryData, id, 0);
  }

  // 暂停播放行程
  const onPause = (id: string) => {
    setIsPlaying(false);
    if (animationMarkerRef.current) {
      animationMarkerRef.current.pauseMove();
    }
  }

  // 继续播放行程
  const onContinue = (id: string) => {
    if (animationMarkerRef.current) {
      animationMarkerRef.current.resumeMove();
    }
  }

  // 终止行程
  const onDestory = (id: string) => {
    if (animationMarkerRef.current) {
      animationMarkerRef.current.stopMove();
      animationMarkerRef.current.hide();
    }
    // 轨迹路线变黑色的清除掉
    if (trackAniRef.current[id]) {
      trackAniRef.current[id].map((item: any) => {
        mapRef.current.removeOverlay(item);
      });
      const newTrackAni = { ...trackAniRef.current };
      delete newTrackAni[id];
      trackAniRef.current = newTrackAni;
    }
    setProgressValue(0)
  }

  // 切换报警类型的勾选
  const toggleAlarmChecked = (checked: boolean, label: number) => {
    setAlarmLabelsChecked({ ...alarmLabelsChecked, [label]: checked });
    const currentAlarmLabels = alarmLabelsRef.current[label];
    if (checked) {
      // 选中显示报警信息浮层
      currentAlarmLabels.forEach((item: any) => {
        mapRef.current.addOverlay(item);
      })
    } else {
      // 取消选中隐藏报警浮层
      currentAlarmLabels.forEach((item: any) => {
        mapRef.current.removeOverlay(item)
      })
    }
  }

  // 取消所有报警类型的勾选
  const cancelAllAlarmChecked = () => {
    if (animationMarkerRef.current) {
      animationMarkerRef.current.stopMove();
      animationMarkerRef.current.hide();
    }
    // 清除测距工具所画的线
    ruleRef.current.forEach((item: any) => {
      // 百度地图暂不支持尺子工具, 此处为兼容保留
    });
    ruleRef.current = [];
    alarmLabelsRef.current = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 8: [], 9: [] };
    // 清空所有轨迹动画
    trackAniRef.current = {};
    // 清除标注点
    if (mapRef.current) {
      mapRef.current.clearOverlays();
    }
    setAlarmLabelsChecked({ 1: false, 2: false, 3: false, 4: false, 5: false, 6: false, 8: false, 9: false });
  }

  // 渲染行程时间
  const renderItineraryTime = (value: any) => {
    const startTime = formatTime(value[0]?.reportedTime, 'YYYY-MM-DD HH:mm:ss', '无');
    const endTime = formatTime(value[value.length - 1]?.reportedTime, 'YYYY-MM-DD HH:mm:ss', '无');
    return `起始时间：${startTime}，结束时间：${endTime}`;
  }

  //抽屉打开 
  const showDrawer = () => {
    setIsPlaying(false); // 设置播放状态为false
    form.setFieldsValue({ travelId: null }) //清空行驶Id
    setCurrentSpeed('')  //清空速度 
    setOpen(true);
    setHasSearch(false);
    setProgressValue(0)
  };

  //抽屉关闭
  const onClose = () => {
    setOpen(false);
    if (itineraryList.length > 0) {
      //得到id push新数组 
      const idArray: any = []
      itineraryList.forEach((item: any) => {
        // console.log('item', item);
        idArray.push({ lable: item.id, value: item.id })
      })
      setTravelId(idArray);
    } else {
      return;
    }
  };
  //表格内对当前行驶Id赋值  
  const travelItineraryId = (value: any) => {
    if (value) {
      setcurrentId(value);
      setVal(value)
      setOpen(false);
      message.success(`查询的行程Id是${value}`);
      //提示选择行驶id成功
      itineraryList.forEach((item: any) => {
        if (item.id === value) {
          setcurrentData(item.gpsData);
          drawLinesOnMap(item.gpsData);
        }
      }
      )
    }
  }

  // 打开行程数据
  const openTripData = () => {
    setOpen(true);
  }

  // 表格列显示
  const columns = [
    {
      title: '行程ID',
      dataIndex: 'id',
      width: 150,
      render: (value: string, item: any) => (
        <div style={{ color: '#007acc', cursor: 'pointer' }} onClick={() => travelItineraryId(value)}> {value}</div>
      )
    },
    {
      title: '时间',
      dataIndex: 'gpsData',
      width: 280,
      render: (value: any) => renderItineraryTime(value)
    },
    {
      title: '停驶位置',
      dataIndex: 'describeLocation',
      width: 150,
    }
  ]

  return (
    <div className={styles.trackPlaybackBox}>
      <div className={styles.carTree}>
        <CarTree
          treeSelect={treeSelect}
          selectableTypes={['car']}
        />
      </div>
      <div className={styles.playbackBox}>
        <div className={styles.mapTitle}>
          地图
          {showSwitch && <Switch className={styles.switchTitle} checkedChildren="ADAS" unCheckedChildren="AEB"
            onChange={adasSwitchover} />}
        </div>
        <LoadingWrapper spinning={loading} autoHeight>
          <div className={styles.mapBox}>
            <div id="trackMap" className={styles.map} />
            <div className={styles.alarmCheckbox} style={{ display: adasSwitch ? 'block' : 'none' }}>
              {['常规报警', 'ADAS报警', 'BSD报警', 'DSM报警'].map((item: string, index: number) => (
                <div key={item}>
                  <Checkbox
                    checked={alarmLabelsChecked[index + 1]}
                    onChange={(e: CheckboxChangeEvent) => toggleAlarmChecked(e.target.checked, index + 1)}
                  >
                    {item}
                  </Checkbox>
                </div>
              ))}
            </div>
            <div className={styles.alarmCheckbox} style={{ display: adasSwitch ? 'none' : 'block' }}>
              {['FCW预警', 'AEB报警', '起步阻止信息', 'LDW预警信息', 'HMW预警信息', '其他', '', 'BSD预警', 'BSD制动'].map((item: string, index: number) => (
                <div key={item || `empty-${index}`}>
                  {item && (
                    <Checkbox
                      checked={alarmLabelsChecked[index + 1]}
                      onChange={(e: CheckboxChangeEvent) => toggleAlarmChecked(e.target.checked, index + 1)}
                    >
                      {item}
                    </Checkbox>
                  )}
                </div>
              ))}
            </div>
          </div>
        </LoadingWrapper>
        <Slider defaultValue={0} value={progressValue} max={progressMaxValue} onChange={dragProgress} />
        <div className={styles.box}>
          <Button style={{ width: 100 }} type="dashed">速度：{currentSpeed}</Button>
          <PlayCircleFilled className={styles.myCustomIcon} onClick={() => onPlay(currentId, currentData)} />
          <PauseCircleFilled className={styles.myCustomIcon} onClick={() => onPause(currentId)} />
          <FastForwardFilled className={styles.myCustomIcon} onClick={() => onContinue(currentId)} />
          <CloseCircleFilled className={styles.myCustomIcon} onClick={() => onDestory(currentId)} />
        </div>
        <Form labelCol={{ span: 8 }} wrapperCol={{ span: 26 }} onFinish={onSearch} form={form} autoComplete="off"
          style={{ marginTop: '16px' }}>
          <Row>
            <Col span={7}>
              <Form.Item label="时间" name="trickDate" initialValue={dayjs()}>
                <DatePicker allowClear={false} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item label="车牌号" name="plateNumber">
                <Input onChange={(e) => { const newV = e.target.value.replace(/\s+/g, ''); form.setFieldValue('plateNumber', newV) }} />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item wrapperCol={{ span: 24 }}>
                <div className={styles.buttonBox}>
                  <Button type="primary" htmlType="submit" onClick={showDrawer}>查找行程</Button>
                  <Button type="primary" onClick={openRule}>尺子工具</Button>
                  <ExportButton
                    url={gpsListExportPath}
                    fileName='gps'
                    getParamsFunc={getExportParams}
                    permissionKey="/car-admin/track-playback/export"
                  />
                  <FullscreenOutlined className={styles.myCustomIcon} onClick={openTripData} />
                </div>
              </Form.Item>
            </Col>
          </Row>
          {/* <Row>
            <Col span={7}>  
              <Form.Item label="行驶Id:" style={{ marginLeft: '16px' }} name="travelId" >
                <Select  
                 allowClear 
                 placeholder='请选择行驶Id'  
                 options={travelId}  
                 optionFilterProp="label" 
                 onSelect={handleChange}
                  />
              </Form.Item>
            </Col>
          </Row> */}
        </Form>
      </div>
      <Drawer title="车辆行程列表" width={400} placement={'bottom'} onClose={onClose} open={open}>
        <div>
          {hasSearch ? <div className={styles.tableBox}>
            <div style={{ marginBottom: '10px', display: 'flex' }}>
              <div>运营开始时间：<span style={{ fontSize: '16px', marginRight: '10px' }}>{operationStartTime} </span></div>
              <div>运营停驶时间：<span style={{ fontSize: '16px', marginRight: '10px' }}>{operationStoptTime} </span></div>
              <div>运行时长<span style={{ fontSize: '16px', marginRight: '10px' }}>{runningTime} </span></div>
              <div>停驶时长<span style={{ fontSize: '16px', marginRight: '10px' }}>{downTime} </span></div>
              <div>停驶次数<span style={{ fontSize: '16px', marginRight: '10px' }}>{stopTimes} </span></div>
              <div>最长停驶时长<span style={{ fontSize: '16px', marginRight: '10px' }}>{MaxtopTimes} </span></div>
              <div>最短停驶时长<span style={{ fontSize: '16px', marginRight: '10px' }}>{MinStopTimes} </span></div>
            </div>
            <div style={{ height: '10px' }}></div>
            <CommonTable
              rowKey="id"
              columns={columns}
              dataSource={itineraryList}
              bordered
              loading={loading}
              pagination={false}
              scroll={{ y: 400 }}
              columnFilter={false}

            />
          </div> :
            <div className={styles.tipsBox}
              style={{ fontSize: '20px', fontWeight: 'bold' }}>请输入车牌号，点击查找行程后，进行轨迹回放</div>}</div>
      </Drawer>
    </div>
  )
}

export default TrackPlayback;